export interface ShortcutPart {
  readonly type: 'kbd' | 'text';
  readonly value: string;
}

export interface ShortcutRow {
  readonly label: string;
  readonly icon?: string;
  readonly note?: readonly ShortcutPart[];
  readonly win: readonly ShortcutPart[];
  readonly mac: readonly ShortcutPart[];
}

export interface ShortcutSection {
  readonly title: string;
  readonly rows: readonly ShortcutRow[];
}

const singleKbd = (
  value: string,
  getKeyFromCode?: GetKeyFromCodeFn,
  code?: string,
): ShortcutPart => {
  return kbd('', value, getKeyFromCode, code);
};

const kbd = (
  modifiers: string,
  value: string,
  getKeyFromCode?: GetKeyFromCodeFn,
  code?: string,
): ShortcutPart => {
  let key = value;
  if (code && getKeyFromCode) {
    key = getKeyFromCode(code, value);
  }
  return { type: 'kbd', value: modifiers + key };
};
const text = (value: string): ShortcutPart => ({ type: 'text', value });

type ShortcutColumns = Pick<ShortcutRow, 'win' | 'mac'>;

type GetKeyFromCodeFn = (code: string, fallback: string) => string;

type GetKeyboardLayout = () => string | undefined;

/** Ctrl + key on Windows, ⌘ + key on Mac */
const ctrl = (
  key: string,
  getKeyFromCode?: GetKeyFromCodeFn,
  code?: string,
): ShortcutColumns => ({
  win: [kbd('Ctrl + ', key, getKeyFromCode, code)],
  mac: [kbd('⌘ + ', key, getKeyFromCode, code)],
});

/** Ctrl + Shift + key on Windows, ⌘ + Shift + key on Mac */
const ctrlShift = (
  key: string,
  getKeyFromCode?: GetKeyFromCodeFn,
  code?: string,
): ShortcutColumns => ({
  win: [kbd('Ctrl + Shift + ', key, getKeyFromCode, code)],
  mac: [kbd('⌘ + Shift + ', key, getKeyFromCode, code)],
});

/** Alt + key on Windows, Option + key on Mac */
const alt = (
  key: string,
  getKeyFromCode?: GetKeyFromCodeFn,
  code?: string,
): ShortcutColumns => ({
  win: [kbd('Alt + ', key, getKeyFromCode, code)],
  mac: [kbd('Option + ', key, getKeyFromCode, code)],
});

/** Same shortcut parts on both platforms */
const same = (...parts: ShortcutPart[]): ShortcutColumns => ({
  win: parts,
  mac: parts,
});

export const getShortcutSections = (
  getKeyFromCode: GetKeyFromCodeFn,
): ShortcutSection[] => [
  {
    title: 'general',
    rows: [
      { label: 'undo', ...ctrl('Z') },
      {
        label: 'redo',
        win: [
          kbd('Ctrl + ', 'Y'),
          text('separator'),
          kbd('Ctrl + Shift + ', 'Z'),
        ],
        mac: [kbd('⌘ + Shift + ', 'Z')],
      },
      { label: 'autosave', ...ctrl('S') },
      {
        label: 'zoomInOut',
        ...same(singleKbd('Ctrl'), text('holdMouseWheel')),
      },
      {
        label: 'zoomIn',
        ...ctrl('+'),
      },
      {
        label: 'zoomOut',
        ...ctrl('-'),
      },
      {
        label: 'zoomTo100',
        win: [kbd('Ctrl + ', '0'), text('separator'), kbd('Alt + ', '0')],
        mac: [kbd('⌘ + ', '0'), text('separator'), kbd('Option + ', '0')],
      },
      {
        label: 'fitToScreen',
        ...alt('1'),
      },
      {
        label: 'zoomToSelection',
        ...alt('2'),
      },
      {
        label: 'fullscreen',
        icon: 'ui-arrow-expand',
        ...same(singleKbd('F11')),
      },
      { label: 'verticalScrolling', ...same(text('mouseWheel')) },
      {
        label: 'horizontalScrolling',
        ...same(singleKbd('Shift'), text('holdMouseWheel')),
      },
      {
        label: 'renamingAnElement',
        note: [text('renamingNote'), singleKbd('Esc')],
        ...same(text('selectElementAndHit'), singleKbd('F2')),
      },
      {
        label: 'startTextBlockCreate',
        icon: 'ui-text',
        ...same(singleKbd('T')),
      },
      {
        label: 'startStickyCreate',
        icon: 'ui-sticky',
        ...same(singleKbd('S')),
      },
      {
        label: 'startRectangleCreate',
        icon: 'ui-shapes',
        ...same(singleKbd('R')),
      },
      {
        label: 'startCircleCreate',
        icon: 'ui-shapes',
        ...same(singleKbd('O')),
      },
      {
        label: 'openBDCoreMenu',
        icon: 'ui-formal',
        ...same(singleKbd('C')),
      },
      {
        label: 'openFromRepoMenu',
        icon: 'ui-repository',
        ...same(kbd('Shift + ', 'R')),
      },
    ],
  },
  {
    title: 'customization',
    rows: [
      { label: 'toggleBold', icon: 'ui-bold', ...ctrl('B') },
      { label: 'toggleItalic', icon: 'ui-italic', ...ctrl('I') },
      { label: 'toggleUnderline', icon: 'ui-underline', ...ctrl('U') },
      {
        label: 'toggleStrikethrough',
        icon: 'ui-strikethrough',
        ...ctrlShift('S'),
      },
    ],
  },
  {
    title: 'selection',
    rows: [
      { label: 'selectAll', ...ctrl('A') },
      { label: 'deselectAll', ...ctrlShift('A') },
      {
        label: 'selectAllOfSameType',
        win: [text('selectObjectPlus'), kbd('Ctrl + ', 'E')],
        mac: [text('selectObjectPlus'), kbd('⌘ + ', 'E')],
      },
    ],
  },
  {
    title: 'layering',
    rows: [
      {
        label: 'moveToFront',
        ...same(singleKbd(']', getKeyFromCode, 'BracketRight')),
      },
      {
        label: 'moveForward',
        ...ctrl(']', getKeyFromCode, 'BracketRight'),
      },
      {
        label: 'sendToBack',
        ...same(singleKbd('[', getKeyFromCode, 'BracketLeft')),
      },
      {
        label: 'sendBackward',
        ...ctrl('[', getKeyFromCode, 'BracketLeft'),
      },
    ],
  },
  {
    title: 'otherOperations',
    rows: [
      {
        label: 'proportionalResizing',
        win: [text('hold'), singleKbd('Ctrl'), text('whileResizing')],
        mac: [text('hold'), singleKbd('Option'), text('whenResizing')],
      },
      {
        label: 'reverseLink',
        win: [kbd('Ctrl + ', 'R')],
        mac: [kbd('⌘ + ', 'R')],
      },
      {
        label: 'nestObject',
        note: [text('beforeReleasingNote')],
        ...same(
          text('dragObjectAndPress'),
          singleKbd('Shift'),
          text('beforeReleasing'),
        ),
      },
      { label: 'copySelection', ...ctrl('C') },
      { label: 'pasteElements', ...ctrl('V') },
      { label: 'duplicateSelection', ...ctrl('D') },
      {
        label: 'copyDragSelection',
        win: [text('hold'), singleKbd('Alt'), text('leftMouseDrag')],
        mac: [text('hold'), singleKbd('Option'), text('leftMouseDrag')],
      },
      {
        label: 'openHyperlink',
        win: [singleKbd('Alt'), text('leftMouseClick')],
        mac: [singleKbd('Option'), text('leftMouseClick')],
      },
    ],
  },
];
